import { clsx } from "clsx";
import React, { useRef, useState, useEffect } from "react";
import styles from "../llm-summary.module.scss";

import { useLLMSummary } from "../../../../../hooks/useLLMSummary";
import { LLMSummaryStatus } from "../@types/LLMSummary";
import { useOnClickOutside } from "../../../../../../../hooks";
import LLMSummaryFeedbackButton from "./LLMSummaryFeedbackButton";
import LLMSummarySidebarHeader from "./LLMSummarySidebarHeader";
import LLMSummarySidebarContent from "./LLMSummarySidebarContent";

interface ILLMSummaryPanel {
  isOpen: boolean;
  isEnabled: boolean;
  onClose: () => void;
  onShowFeedbackModal: () => void;
}

const LLMSummaryPanel = ({
  isOpen,
  isEnabled,
  onClose,
  onShowFeedbackModal
}: ILLMSummaryPanel) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [scrollHeight, setScrollHeight] = useState(0);

  const {
    status,
    summary,
    refetchLLMSummary,
    generateLLMSummary,
    summaryIsLoading,
    hasChanged,
    isError
  } = useLLMSummary();

  useEffect(() => {
    if (
      isEnabled &&
      status === LLMSummaryStatus.NOT_FOUND &&
      !summaryIsLoading
    ) {
      generateLLMSummary();
    }
  }, [generateLLMSummary, isEnabled, status, summaryIsLoading]);

  useOnClickOutside(panelRef, onClose, !isOpen);

  const handleScroll = () => {
    const scrollY = window.scrollY;
    const heightIncrease = Math.min(scrollY, 60);
    setScrollHeight(heightIncrease);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const shouldShowSummary =
    [LLMSummaryStatus.DONE, LLMSummaryStatus.PROCESSING].includes(status) &&
    summary;

  const shouldShowRefreshButton =
    [LLMSummaryStatus.DONE, LLMSummaryStatus.ERROR].includes(status) &&
    !summaryIsLoading;

  const shouldShowError = status === LLMSummaryStatus.ERROR;

  const shouldShowFeedback =
    !summaryIsLoading && status === LLMSummaryStatus.DONE && !isError;

  return (
    <div
      ref={panelRef}
      className={clsx(styles.llm_summary_panel, isOpen && styles.open)}
      style={{
        height: `calc(100vh - 100px + ${scrollHeight}px)`
      }}
    >
      {shouldShowFeedback && (
        <LLMSummaryFeedbackButton
          onClick={() => {
            onShowFeedbackModal();
            onClose();
          }}
        />
      )}

      <LLMSummarySidebarHeader
        hasChanged={hasChanged}
        shouldShowRefreshButton={shouldShowRefreshButton}
        onRefreshClick={refetchLLMSummary}
        onCloseClick={onClose}
      />

      <LLMSummarySidebarContent
        summary={summary}
        summaryIsLoading={summaryIsLoading}
        shouldShowError={shouldShowError}
        shouldShowSummary={shouldShowSummary as boolean}
        isProcessing={status === LLMSummaryStatus.PROCESSING}
      />
    </div>
  );
};

export default LLMSummaryPanel;
